import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CCard, CCardBody } from "@coreui/react-pro";
import { searchHelp } from "./search";
import { useHelpArticles } from "./useHelpArticles";
import { helpArticlePath, routePaths } from "../../routes/route-paths";

/**
 * Shown when /help/<slug> names an article that does not exist.
 *
 * Deliberately NOT a redirect to the index. Strategy tooltips are baked into
 * distributed templates and cannot be recalled, so a wrong URL is something we
 * need to find out about — and a silent redirect makes every broken tooltip
 * look like a working one. Staying on the URL keeps it visible in the address
 * bar, diagnosable from a support conversation, and reportable to telemetry
 * once WI #300 lands.
 *
 * The customer still gets somewhere useful: the slug is fed through the same
 * search the index uses, so a near-miss like /help/traderpost-setup surfaces
 * the article that was meant.
 */
export function HelpArticleNotFound({ slug }: { slug?: string }) {
  const { t } = useTranslation();
  const helpArticles = useHelpArticles();

  // Slugs are hyphenated, so they read as a query once the hyphens go.
  const suggestions = slug
    ? searchHelp(helpArticles, slug.replace(/-/g, " ")).slice(0, 3)
    : [];

  return (
    <div className="help-article-page">
      <Link
        to={routePaths.help}
        className="btn btn-sm btn-outline-secondary mb-4"
      >
        {t("help:article.backToIndex")}
      </Link>

      <h1 className="mb-2">{t("help:notFound.title")}</h1>

      <p className="text-body-secondary mb-4">
        {slug ? (
          <>
            {t("help:notFound.bodyWithSlug")}{" "}
            <code>{helpArticlePath(slug)}</code>
          </>
        ) : (
          t("help:notFound.body")
        )}
      </p>

      {suggestions.length > 0 && (
        <CCard className="mb-4">
          <CCardBody>
            <p className="fw-semibold mb-3">{t("help:notFound.didYouMean")}</p>
            <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
              {suggestions.map(({ article }) => (
                <li key={article.slug}>
                  <Link
                    to={helpArticlePath(article.slug)}
                    className="text-decoration-none"
                  >
                    {article.title}
                  </Link>
                  <span className="text-body-secondary small d-block">
                    {article.summary}
                  </span>
                </li>
              ))}
            </ul>
          </CCardBody>
        </CCard>
      )}

      <div className="d-flex flex-wrap gap-2">
        <Link to={routePaths.help} className="btn btn-primary">
          {t("help:notFound.browseAll")}
        </Link>
        <Link to={routePaths.contact} className="btn btn-outline-secondary">
          {t("help:index.contactSupport")}
        </Link>
      </div>
    </div>
  );
}
